// #753 — Settings sub-page. Bundles the per-user controls that used to
// be scattered through /admin/users/{id}:
//   - Quota controls    (override / reset / set monthly token usage)
//   - TTS provider      (per-user override for A/B voice-quality eval)
//   - Audio profile     (read-only speaker loudness band — #382)
//   - Opt-in mic-audio  (per-user voice-clip capture toggle — #597)
//
// The first three were inline components inside AdminUserDetail.js;
// they moved here as part of the tile migration so the main page
// can stay a thin dashboard.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import AudioRecordingToggle from './AudioRecordingToggle';
import { userSubPageStyles } from './userSubPageStyles';

const VERSION = 'v1.1.0';

// ── Quota controls (#302) ─────────────────────────────────────────────────
//
// Override the per-user cap, reset current-month usage to zero, or set it
// to a specific value (testing convenience for verifying threshold
// crossings). Collapsed by default — click "Manage quota" to expand.
// `user.monthly_quota_override` is the LITERAL stored value (null = no
// override); `user.monthly_quota` is the resolved cap (env default falls
// through when no override is set). Using the override field on hydration
// keeps the form blank when there's no per-user setting.
function QuotaControls({ userId, user, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [overrideValue, setOverrideValue] = useState(
    user?.monthly_quota_override != null ? String(user.monthly_quota_override) : ''
  );
  const [setUsageValue, setSetUsageValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem('am_admin_token');

  useEffect(() => {
    setOverrideValue(
      user?.monthly_quota_override != null ? String(user.monthly_quota_override) : ''
    );
  }, [user?.monthly_quota_override]);

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const doAction = async (label, fetcher) => {
    setBusy(true); setMsg(null);
    try {
      const res = await fetcher();
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMsg({ kind: 'err', text: err.detail || `${label} failed` });
      } else {
        setMsg({ kind: 'ok', text: `${label} done` });
        if (onChanged) onChanged();
      }
    } catch {
      setMsg({ kind: 'err', text: `${label} failed` });
    }
    setBusy(false);
    setTimeout(() => setMsg(null), 3500);
  };

  const saveOverride = () => {
    const v = overrideValue.trim();
    const body = v === '' ? { override: null } : { override: parseInt(v, 10) };
    doAction('Override saved', () =>
      fetch(`/api/admin/users/${userId}/quota/override`, {
        method: 'POST', headers, body: JSON.stringify(body),
      }));
  };

  const resetUsage = () => {
    if (!window.confirm("Reset this user's monthly token count to zero? (Earlier periods are not touched.)")) return;
    doAction('Usage reset to 0', () =>
      fetch(`/api/admin/users/${userId}/usage/reset`, { method: 'POST', headers }));
  };

  const setUsage = () => {
    const v = parseInt(setUsageValue, 10);
    if (Number.isNaN(v) || v < 0) {
      setMsg({ kind: 'err', text: 'Enter a non-negative integer' });
      return;
    }
    doAction(`Usage set to ${v}`, () =>
      fetch(`/api/admin/users/${userId}/usage/set`, {
        method: 'POST', headers, body: JSON.stringify({ tokens: v }),
      }));
  };

  if (!expanded) {
    return (
      <div style={{
        background: '#fff', borderRadius: 12, padding: '14px 20px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginTop: 8, marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          Quota controls
          {user?.has_override && (
            <span style={{ marginLeft: 10, fontSize: '0.8rem', color: '#3a8fb7', fontWeight: 600 }}>
              · override active ({Number(user.monthly_quota_override).toLocaleString()})
            </span>
          )}
        </div>
        <button onClick={() => setExpanded(true)}
          style={{ padding: '6px 14px', borderRadius: 6, border: '1.5px solid #3a8fb7',
            background: '#fff', color: '#3a8fb7', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
          Manage quota
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginTop: 8, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#2C2C2C' }}>
          Quota controls (admin only)
        </div>
        <button onClick={() => setExpanded(false)}
          style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #C8C4BD',
            background: '#fff', color: '#666', cursor: 'pointer', fontSize: '0.8rem' }}>
          Hide
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
            Per-user override (blank = use system default)
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number" min="0"
              value={overrideValue}
              onChange={e => setOverrideValue(e.target.value)}
              placeholder="e.g. 1000000"
              style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #E0DDD8', borderRadius: 6, fontSize: '0.9rem' }}
            />
            <button onClick={saveOverride} disabled={busy}
              style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#3a8fb7', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
              Save
            </button>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
            Reset current-month usage to zero
          </label>
          <button onClick={resetUsage} disabled={busy}
            style={{ padding: '8px 14px', borderRadius: 6, border: '1.5px solid #C0392B', background: '#fff', color: '#C0392B', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
            Reset usage
          </button>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
            Set current-month usage to (testing — e.g. 399999 to verify 80% warning crossing)
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number" min="0"
              value={setUsageValue}
              onChange={e => setSetUsageValue(e.target.value)}
              placeholder="e.g. 399999"
              style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #E0DDD8', borderRadius: 6, fontSize: '0.9rem' }}
            />
            <button onClick={setUsage} disabled={busy || !setUsageValue}
              style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#888', color: '#fff', fontWeight: 600, cursor: setUsageValue ? 'pointer' : 'not-allowed', fontSize: '0.85rem' }}>
              Set
            </button>
          </div>
        </div>
        {msg && (
          <div style={{ fontSize: '0.85rem', color: msg.kind === 'ok' ? '#27AE60' : '#C0392B', marginTop: 4 }}>
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── TTS provider override (#357) ──────────────────────────────────────────
//
// Per-user provider override. Takes effect on the user's NEXT call —
// changing this won't interrupt anything currently streaming. Backend
// validates language compatibility (#451/#522) and re-maps the voice_id
// across catalogues when the provider changes.
function TtsProviderControl({ userId, user, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem('am_admin_token');

  const current = user?.tts_provider || '';

  const save = async (value) => {
    setBusy(true); setMsg(null);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      const body = JSON.stringify({ provider: value || null });
      const res = await fetch(`/api/admin/users/${userId}/tts-provider`, {
        method: 'POST', headers, body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMsg({ kind: 'err', text: err.detail || 'Save failed' });
      } else {
        setMsg({ kind: 'ok', text: 'TTS provider updated — takes effect on next call' });
        if (onChanged) onChanged();
      }
    } catch {
      setMsg({ kind: 'err', text: 'Save failed' });
    }
    setBusy(false);
    setTimeout(() => setMsg(null), 3500);
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '16px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginTop: 8, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            TTS provider (admin only)
          </div>
          <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: 2 }}>
            Override the default for this user. Useful for A/B voice-quality eval.
          </div>
        </div>
        <select
          value={current}
          onChange={e => save(e.target.value)}
          disabled={busy}
          style={{
            marginLeft: 'auto',
            padding: '8px 12px',
            border: '1.5px solid #C8C4BD',
            borderRadius: 6,
            fontSize: '0.9rem',
            minWidth: 180,
            background: '#fff',
            cursor: busy ? 'wait' : 'pointer',
          }}>
          <option value="">System default</option>
          <option value="azure">Azure Neural</option>
          <option value="elevenlabs">ElevenLabs Multilingual</option>
          <option value="deepgram">Deepgram Aura-2</option>
        </select>
        {msg && (
          <span style={{
            fontSize: '0.82rem',
            color: msg.kind === 'ok' ? '#27AE60' : '#C0392B',
            marginLeft: 12,
          }}>
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Audio profile (#382) ──────────────────────────────────────────────────
//
// Read-only display of the user's speaker loudness band, derived
// server-side from per-turn SNR aggregated as an EWMA. Shows the band,
// sample size, the raw EWMA value (for debugging), and a context line.
function SpeakerProfileCard({ profile }) {
  if (!profile) return null;
  const { ewma, sample_size, band, last_updated } = profile;
  const bandStyles = {
    unknown:   { color: '#666', label: 'Unknown',   description: sample_size === 0 ? 'No measurements yet — speaker profile builds up after the first few calls.' : `Only ${sample_size} measurement${sample_size === 1 ? '' : 's'} so far — at least 3 needed for a reliable band.` },
    whisper:   { color: '#dc2626', label: 'Whisper', description: 'Very quiet speaker. Deepgram likely struggles to transcribe; consider a clip-on mic or moving the tablet closer.' },
    soft:      { color: '#f59e0b', label: 'Soft',    description: 'Quiet speaker. Recovery prompts will hint at speaking up. A clip-on mic or moving the tablet closer often helps.' },
    normal:    { color: '#10b981', label: 'Normal',  description: 'Comfortable conversational level. No action needed.' },
    loud:      { color: '#3b82f6', label: 'Loud',    description: 'Above-average projection. No action needed.' },
    shouting:  { color: '#7c2d12', label: 'Shouting', description: 'Sustained very loud audio — possibly TV / radio in the background. Worth checking on the user.' },
  };
  const style = bandStyles[band] || bandStyles.unknown;
  const lastUpdatedDate = last_updated ? new Date(last_updated) : null;
  return (
    <div style={{
      marginTop: 8, marginBottom: 16, padding: '14px 16px',
      background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8,
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8, color: '#374151' }}>
        Audio profile
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{
          display: 'inline-block', padding: '4px 12px', borderRadius: 999,
          background: style.color, color: 'white',
          fontWeight: 600, fontSize: '0.85rem', letterSpacing: '0.04em',
        }}>{style.label}</div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {sample_size} call{sample_size === 1 ? '' : 's'} measured
          {ewma !== null && <span> · SNR ≈ {ewma.toFixed(1)}</span>}
          {lastUpdatedDate && <span> · last updated {lastUpdatedDate.toLocaleDateString()}</span>}
        </div>
      </div>
      <div style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 8, lineHeight: 1.4 }}>
        {style.description}
      </div>
    </div>
  );
}

// ── Transcription quality (#986) ────────────────────────────────────────────
// Read-only per-user STT quality + performance summary over a recent window:
// Deepgram confidence percentiles + STT-latency percentiles + counts of
// low-confidence / slow turns. Sits beside the #382 Audio profile (SNR) card.
// Aggregated server-side from turn_diagnostics (#702) — see
// services/stt_quality_profile.py.
function TranscriptionQualityCard({ stt }) {
  if (!stt) return null;
  const {
    sample_size, confidence_p50, confidence_p10,
    latency_p50_ms, latency_p95_ms,
    low_confidence_count, slow_count,
    low_confidence_threshold, slow_latency_ms, window_days,
  } = stt;
  const flagged = (low_confidence_count || 0) + (slow_count || 0);
  const amberCeiling = Math.max(1, Math.round((sample_size || 0) * 0.1));
  const tone = flagged === 0 ? '#10b981' : (flagged <= amberCeiling ? '#f59e0b' : '#dc2626');
  const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${(v * 100).toFixed(0)}%`;
  const fmtMs = (v) => (v === null || v === undefined) ? '—' : `${v} ms`;
  return (
    <div style={{
      marginTop: 8, marginBottom: 16, padding: '14px 16px',
      background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8,
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8, color: '#374151' }}>
        Transcription quality
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{
          display: 'inline-block', padding: '4px 12px', borderRadius: 999,
          background: tone, color: 'white', fontWeight: 600, fontSize: '0.85rem',
        }}>{flagged === 0 ? 'Healthy' : `${flagged} flagged`}</div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {sample_size} turn{sample_size === 1 ? '' : 's'} · last {window_days}d
        </div>
      </div>
      <div style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 10, lineHeight: 1.5 }}>
        <div>Confidence: p50 <b>{fmtPct(confidence_p50)}</b> · p10 <b>{fmtPct(confidence_p10)}</b></div>
        <div>STT latency: p50 <b>{fmtMs(latency_p50_ms)}</b> · p95 <b>{fmtMs(latency_p95_ms)}</b></div>
        <div style={{ marginTop: 6 }}>
          <span style={{ color: low_confidence_count ? '#b45309' : '#6b7280' }}>
            {low_confidence_count} low-confidence (&lt;{fmtPct(low_confidence_threshold)})
          </span>
          {' · '}
          <span style={{ color: slow_count ? '#b45309' : '#6b7280' }}>
            {slow_count} slow (&gt;{slow_latency_ms} ms)
          </span>
        </div>
        <div style={{ marginTop: 6, color: '#9ca3af', fontSize: '0.8rem' }}>
          Per-turn transcript + confidence + audio clip live in Call Diagnostics.
          Confidence is engine-dependent (Flux end-of-turn vs nova transcript) —
          read it as a trend, not an absolute.
        </div>
      </div>
    </div>
  );
}

export default function AdminUserSettings() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extracted so child controls can trigger a re-fetch after a save
  // (matches the original AdminUserDetail pattern).
  async function fetchUser() {
    try {
      const u = await adminApi.getUser(id);
      setUser(u);
      return u;
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    fetchUser().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout title="Settings">
      <style>{userSubPageStyles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <>
          <div className="page-header">
            <div className="page-title">
              {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
            </div>
            <div className="page-subtitle">
              {user.email} · Per-user settings sub-page
            </div>
          </div>

          <div className="section-title" style={{ marginTop: 0 }}>Quota</div>
          <QuotaControls userId={id} user={user} onChanged={fetchUser} />

          <div className="section-title">TTS provider</div>
          <TtsProviderControl userId={id} user={user} onChanged={fetchUser} />

          <div className="section-title">Audio profile</div>
          <SpeakerProfileCard profile={user.speaker_profile} />

          <div className="section-title">Transcription quality</div>
          <TranscriptionQualityCard stt={user.stt_quality} />

          <div className="section-title">Mic-audio recording</div>
          {user.elderly_user_id && (
            <AudioRecordingToggle userId={user.elderly_user_id} />
          )}
        </>
      )}
    </AdminLayout>
  );
}
